import { useContext, useMemo, useReducer, useState } from "react";

import {
  DOCUMENT_FORM_NAME_BALANCE_SHEET,
  DOCUMENT_FORM_NAME_BASE_ACRES,
  DOCUMENT_FORM_NAME_DEBT_SCHEDULE,
  DOCUMENT_FORM_NAME_EQUIPMENT_INVENTORY,
  DOCUMENT_FORM_NAME_FARMS,
  DOCUMENT_FORM_NAME_INCOME_STATEMENT,
  DOCUMENT_FORM_NAME_SCHEDULE_F,
  handleProcessExcelForm,
  parseExcelForm,
} from "@datanac/datanac-api-toolkit";
import { useDialog } from "@datanac/datanac-mui-toolkit";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import SaveOutlined from "@mui/icons-material/SaveOutlined";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import {
  CONTENT_TYPE_EXCEL_XLS,
  CONTENT_TYPE_EXCEL_XLSX,
  CONTENT_TYPE_PDF,
  getFileIcon,
  getFileTypeName,
  uploadDocument,
} from "api-toolkit/FilesHelper";
import { ApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { FINANCIAL_DOCUMENTS } from "components/Financial/FinancialHelpers";
import TilleyDataGrid from "components/shared/DataGrid/TilleyDataGrid";
import { FileUploadPanel } from "mui-toolkit/upload/FileUploadPanel";
import { useSnackbar } from "notistack";
import { v4 as uuidv4 } from "uuid";

import { getTruncatedFilename } from "../../../../../api-toolkit/FilesHelper";
import { CloseIcon, DeleteIcon } from "../../../../Icons/Icons";
import { FileUploadHandlers, importTransformedDocument, ProcessImportFile } from "./ImportService.js";

export default function ImportUploadFileDialog({ year, onClose }) {
  const { globalState } = useContext(AppContext);

  const { component: confirmDialogComponent } = useDialog();
  const { enqueueSnackbar } = useSnackbar();

  // ---

  const [fileContents, setFileContents] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState(null);
  const handleFileUploadOnChange = async (_currentFile) => {
    if (_currentFile?.type === CONTENT_TYPE_PDF) {
      setFile(_currentFile);
    } else if (_currentFile?.type === CONTENT_TYPE_EXCEL_XLS || _currentFile?.type === CONTENT_TYPE_EXCEL_XLSX) {
      try {
        setIsLoading(true);
        const fileBuffer = await _currentFile.arrayBuffer();
        const _parsedExcelFormData = await parseExcelForm(fileBuffer);
        const processedFormData = await handleProcessExcelForm(_parsedExcelFormData, ApiHelper);

        if (processedFormData?.dataItems?.length) {
          setFileContents(processedFormData);
          setFile(_currentFile);
        } else {
          setFile(_currentFile);
        }

        if (processedFormData?.formName) {
          dispatch({
            type: "update",
            payload: { key: "document_type_name", value: processedFormData?.formName },
          });
        }
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        console.warn(err);
        handleFileUploadOnError(err);
      }
    }

    setEventErrors([]);
  };

  const [eventErrors, setEventErrors] = useState([]);
  const handleFileUploadOnError = (err) => {
    console.warn(err);
    setEventErrors(["Error opening file. Please select a valid file and try again."]);
  };

  const handleReset = () => {
    setFile(null);
    setFileContents(null);
    setEventErrors([]);
    // clear document type selection
    dispatch({ type: "update", payload: { key: "document_type_name", value: "" } });
  };

  // ---

  const [state, dispatch] = useReducer(propUpdateReducer);

  const handleChangeStateSelect = (event) => {
    dispatch({
      type: "update",
      payload: { key: event.target.name, value: event.target.value },
    });
  };

  const FORM_NAMES_SUPPORTED = [
    DOCUMENT_FORM_NAME_FARMS,
    DOCUMENT_FORM_NAME_BASE_ACRES,
    DOCUMENT_FORM_NAME_BALANCE_SHEET,
    DOCUMENT_FORM_NAME_INCOME_STATEMENT,
    DOCUMENT_FORM_NAME_SCHEDULE_F,
    DOCUMENT_FORM_NAME_EQUIPMENT_INVENTORY,
    DOCUMENT_FORM_NAME_DEBT_SCHEDULE,
  ];

  const isValid = useMemo(() => {
    let isValid = false;

    if (fileContents?.dataItems?.length) {
      return FORM_NAMES_SUPPORTED.includes(fileContents?.formName);
    } else if (file) {
      return state?.document_type_name;
    }

    return isValid;
  }, [state, fileContents, file]);

  const validationErrors = useMemo(() => {
    const _validationErrors = [];
    if (!isValid) {
      if (fileContents && !FORM_NAMES_SUPPORTED.includes(fileContents?.formName)) {
        _validationErrors.push(
          `Unsupported file type (${fileContents?.formName}). You can add this file to your Farm Vault, but the data cannot be automatically imported.`
        );
      }
    }

    return _validationErrors || [];
  }, [isValid]);

  // ---
  const notifyUploadError = (error, filename) => {
    console.warn(error);
    const detail = error?.response?.data?.detail || error?.detail || "";
    enqueueSnackbar(
      `Error uploading ${filename ? `file ${filename}` : "a file"}. Please select another file or try again. ${detail}`,
      {
        preventDuplicate: true,
        variant: "error",
      }
    );
  };

  const handleSaveClick = async () => {
    const _document_id = uuidv4();
    const _document = {
      id: _document_id,
      year: year,
      document_type_name: state?.document_type_name,
      content_type: file?.type,
      is_active: true,
      file_name: file?.name,
    };

    // Handle file contents with known handlers — route through importTransformedDocument for all types
    if (fileContents?.formName && FileUploadHandlers[fileContents.formName]) {
      try {
        const result = await importTransformedDocument(
          fileContents.formName,
          fileContents.dataItems,
          _document_id,
          globalState,
          year ?? null
        );
        if (result.success) {
          onClose?.();
        } else if (result.isInfo) {
          // Type not in switch: fall back to ProcessImportFile + handler (e.g. Balance Sheet, Equipment, Debt Schedule)
          const _uploadFormData = ProcessImportFile(fileContents, globalState, _document_id);
          const handlerConfig = FileUploadHandlers[fileContents.formName];
          await handlerConfig.handler(_uploadFormData, globalState);
          onClose?.();
        } else {
          notifyUploadError(new Error(result.message), file?.name);
        }
      } catch (err) {
        notifyUploadError(err, file?.name);
      }
    }

    // Handle file upload for document storage
    if (file) {
      await uploadDocument(_document, file, globalState);
      onClose?.();
    }
  };

  const columns = useMemo(
    () =>
      fileContents?.dataItems[0]
        ? Object.keys(fileContents?.dataItems[0]).map((key) => ({
            field: key,
            headerName: key,
            flex: 1,
            minWidth: 150,
          }))
        : [],
    [fileContents]
  );

  // ---

  return (
    <>
      <DialogTitle color="textPrimary" sx={{ fontWeight: "700 !important" }}>
        Upload Files
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {(!fileContents?.dataItems?.length || !isValid) && !file && (
          <FileUploadPanel onChange={handleFileUploadOnChange} onError={handleFileUploadOnError} />
        )}

        {Boolean(file) && (
          <Box>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "colors.borderLightGray",
                borderRadius: "12px",
                padding: 2,
                position: "relative",
                width: "100%",
                maxWidth: 400,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 200,
                margin: "15px auto",
              }}
            >
              {/* Delete Icon Top Right */}
              <IconButton size="small" sx={{ position: "absolute", top: 8, right: 8 }} onClick={handleReset}>
                <DeleteIcon />
              </IconButton>
              <Stack direction="column" spacing={2} alignItems="center">
                {getFileIcon(file)}
                <Typography>{getFileTypeName(file)}</Typography>
                <Typography sx={{ textAlign: "center" }}>{getTruncatedFilename(file?.name, 50)}</Typography>
              </Stack>
            </Box>
            <br />

            <InputLabel id="document_type_name_label">Select Document Type:</InputLabel>
            <Select
              fullWidth
              labelId="document_type_name_label"
              value={state?.document_type_name || ""}
              name={"document_type_name"}
              onChange={handleChangeStateSelect}
              required={true}
              label={"Document Type"}
            >
              {FINANCIAL_DOCUMENTS?.map((currentItem, index) => {
                return (
                  <MenuItem key={index} value={currentItem?.document_type_name}>
                    {currentItem?.document_type_name}
                  </MenuItem>
                );
              })}
            </Select>
          </Box>
        )}

        <List>
          {[...validationErrors, ...eventErrors].map((err, index) => (
            <ListItem key={index}>
              <ListItemAvatar>
                <ErrorOutline color="error" />
              </ListItemAvatar>
              <ListItemText>
                <Typography color="error">{err}</Typography>
              </ListItemText>
            </ListItem>
          ))}
        </List>

        {Boolean(fileContents?.dataItems?.length) && (
          <>
            <Typography variant="h1">{fileContents?.formName}</Typography>
            <div className="import-file-datagrid-container" style={{ display: "flex", flexDirection: "column" }}>
              <TilleyDataGrid
                rows={fileContents?.dataItems.map((row, index) => ({
                  id: index,
                  ...row,
                }))}
                columns={columns}
                pageSizeOptions={[5, 10, 25]}
                defaultPageSize={5}
                gridKey="import-file-contents"
                getRowId={(row) => row.id}
                isLoading={isLoading}
              />
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          sx={{
            justifyContent: {
              xs: "center",
              sm: "flex-end",
            },
            width: "100%",
          }}
        >
          <Button variant="contained" color="secondary" autoFocus onClick={onClose}>
            <CloseOutlined sx={{ mr: 1 }} />
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSaveClick} disabled={!isValid}>
            <SaveOutlined sx={{ mr: 1 }} />
            Save
          </Button>
        </Stack>
      </DialogActions>

      {confirmDialogComponent}
    </>
  );
}
